import { shopItemsUrl, shopPageQuery, type CatalogKind, type Page, type PublicShopItem } from "@fusiqo/vozqi-shared";
import { useEffect, useState } from "react";

/** How many cards «Más servicios» and «Más productos» show: a taste, the profile has the rest. */
export const MORE_ITEMS_SHOWN = 4;

export type MoreItems = {
  items: PublicShopItem[];
  /** The page that listed them, which vouches for their photos (`coverSrc`). */
  pageQuery: string;
  /** The profile lists more than these: «Ver todos». */
  more: boolean;
};

/**
 * The business's other items of the same kind, for the foot of an item's
 * page: the first page the profile itself lists -- the same query,
 * so the same Worker cache entry --, without the item on screen. Nothing on a
 * failure: the part is simply not drawn.
 */
export function useMoreItems(slug: string | null, kind: CatalogKind | null, token: string): MoreItems | null {
  const [more, setMore] = useState<MoreItems | null>(null);

  useEffect(() => {
    if (slug === null) return;
    const controller = new AbortController();
    void (async () => {
      try {
        const response = await fetch(shopItemsUrl(slug, null, "", null, kind), { signal: controller.signal });
        if (!response.ok) return;
        const page = (await response.json()) as Page<PublicShopItem>;
        const others = page.items.filter((item) => item.token !== token);
        setMore({
          items: others.slice(0, MORE_ITEMS_SHOWN),
          pageQuery: shopPageQuery(null, "", null, kind),
          more: others.length > MORE_ITEMS_SHOWN || page.nextCursor !== null,
        });
      } catch {
        // A taste of the rest, not the page: without it the page ends sooner.
      }
    })();
    return () => controller.abort();
  }, [slug, kind, token]);

  return more;
}
